import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  DOMAINS_CONFIG,
  EDUCATION_OPTIONS,
  calculateMiniMentalTotal,
  validateDomainScore
} from '../lib/calculator';
import SafeIcon from '../common/SafeIcon';

/*
  Guia lateral de aplicação (rodada 12). Antes era um "wizard" de 9 etapas que tomava a seção inteira e
  avançava com portão de validação — parecia uma aplicação do teste, mas não mostra (nem pode mostrar) os itens.
  Agora é o que ele realmente é: um painel de consulta ao lado do texto da página, para ser lido enquanto o
  profissional aplica a versão do instrumento adotada na própria prática. As etapas abrem em qualquer ordem
  (lista com acordeão, aria-current na etapa ativa), a soma por domínio fica sempre à vista, e Voltar/Continuar
  existem só como atalho sequencial — nada bloqueia. Não é diálogo modal: a página continua rolável atrás.
*/

const DOMAIN_GUIDANCE = {
  temporalOrientation: {
    evaluates: 'Orientação temporal no momento da avaliação.',
    conduct: 'Conduza a tarefa correspondente exatamente conforme a versão do instrumento adotada em sua prática e registre apenas a pontuação obtida.',
    caution: 'Evite reformular, completar ou antecipar informações que mudem a condição padronizada da tarefa.',
    frequentError: 'Oferecer pistas ou aceitar respostas fora do critério previsto pela versão utilizada.'
  },
  spatialOrientation: {
    evaluates: 'Orientação espacial e reconhecimento do contexto em que a pessoa está sendo avaliada.',
    conduct: 'Aplique a tarefa correspondente conforme a versão validada ou licenciada utilizada e registre o número de pontos obtidos.',
    caution: 'Mantenha a forma de administração prevista na versão adotada.',
    frequentError: 'Modificar a pergunta ou induzir a resposta quando há hesitação.'
  },
  registration: {
    evaluates: 'Capacidade de receber e registrar informações apresentadas durante a avaliação.',
    conduct: 'Use os estímulos definidos pela versão do instrumento utilizada em sua prática e registre o número de respostas corretas.',
    caution: 'Preserve as regras de apresentação e pontuação da versão utilizada.',
    frequentError: 'Dar pistas que alterem a padronização da tarefa.'
  },
  attentionCalculation: {
    evaluates: 'Atenção sustentada, concentração e manipulação mental de informação.',
    conduct: 'Aplique a tarefa de atenção prevista na versão utilizada e registre a pontuação conforme os critérios dessa versão.',
    caution: 'Não substitua a tarefa por uma alternativa criada durante a consulta.',
    frequentError: 'Trocar o método no meio da avaliação ou criar uma regra própria de correção.'
  },
  recall: {
    evaluates: 'Recuperação de informações previamente apresentadas durante a mesma avaliação.',
    conduct: 'Retome a tarefa de evocação prevista na versão em uso e registre a pontuação sem acrescentar novas pistas.',
    caution: 'Evite transformar dificuldade de evocação em tarefa de reconhecimento.',
    frequentError: 'Fornecer ajuda não prevista e depois contabilizar a resposta como espontânea.'
  },
  language: {
    evaluates: 'Aspectos de linguagem avaliados pela versão do instrumento empregada.',
    conduct: 'Siga as tarefas e os critérios de pontuação da versão adotada, sem substituir comandos, frases, figuras ou materiais.',
    caution: 'Barreiras de idioma, audição, alfabetização ou compreensão precisam ser documentadas no contexto clínico.',
    frequentError: 'Adaptar espontaneamente uma tarefa e manter a mesma regra de pontuação.'
  },
  visuoconstruction: {
    evaluates: 'Organização visuoespacial e capacidade visuoconstrutiva conforme a tarefa prevista no instrumento.',
    conduct: 'Utilize exclusivamente o material correspondente à versão validada ou licenciada adotada em sua prática.',
    caution: 'Este guia não apresenta nem substitui a figura do instrumento.',
    frequentError: 'Usar uma figura alternativa e tratá-la como equivalente para a mesma pontuação.'
  }
};

const STEPS = [
  { name: 'Preparação', type: 'preparation' },
  { name: 'Orientação', type: 'domains', domainIds: ['temporalOrientation', 'spatialOrientation'] },
  { name: 'Registro', type: 'domains', domainIds: ['registration'] },
  { name: 'Atenção e cálculo', type: 'domains', domainIds: ['attentionCalculation'] },
  { name: 'Evocação', type: 'domains', domainIds: ['recall'] },
  { name: 'Linguagem', type: 'domains', domainIds: ['language'] },
  { name: 'Capacidade visuoconstrutiva', type: 'domains', domainIds: ['visuoconstruction'] },
  { name: 'Contexto / escolaridade', type: 'context' },
  { name: 'Resultado', type: 'result' }
];
const RESULT_INDEX = STEPS.length - 1;

function capture(name, properties = {}) {
  if (typeof window === 'undefined') return;
  if (window.eidocAnalytics?.capture) window.eidocAnalytics.capture(name, properties);
}

function emptyScores() {
  return Object.fromEntries(DOMAINS_CONFIG.map((domain) => [domain.id, '']));
}

function domainById(id) {
  return DOMAINS_CONFIG.find((item) => item.id === id);
}

// Parcial de uma etapa de domínios ("4/10"); null quando nenhum dos domínios foi informado.
function stepFraction(step, scores) {
  if (step.type !== 'domains') return null;
  const domains = step.domainIds.map(domainById);
  const informed = domains.filter((domain) => scores[domain.id] !== '');
  if (!informed.length) return null;
  const sum = informed.reduce((acc, domain) => acc + Number(scores[domain.id]), 0);
  const max = domains.reduce((acc, domain) => acc + domain.max, 0);
  return `${sum}/${max}`;
}

const stepButtonBase = 'relative -ml-px flex w-full items-center justify-between gap-3 border-l-2 py-2.5 pl-4 pr-2 text-left text-[length:var(--fs-15)] leading-snug';
const navButton = 'inline-flex min-h-11 items-center justify-center px-4 font-semibold rounded-[var(--radius-control)] disabled:cursor-not-allowed disabled:opacity-40';
const fieldClass = 'input-field mt-2';

export default function AssistedApplicationWizard() {
  const [open, setOpen] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [scores, setScores] = useState(emptyScores);
  const [educationContext, setEducationContext] = useState('none');
  const [fieldErrors, setFieldErrors] = useState({});
  const startedRef = useRef(false);
  const completedRef = useRef(false);
  const launcherRef = useRef(null);
  const closeRef = useRef(null);

  const currentStep = STEPS[stepIndex];
  const total = useMemo(() => calculateMiniMentalTotal(scores), [scores]);
  const informedCount = DOMAINS_CONFIG.filter((domain) => scores[domain.id] !== '').length;
  const selectedEducation = EDUCATION_OPTIONS.find((option) => option.value === educationContext);

  // Uma única origem do evento de ida à calculadora (dois links, um mecanismo).
  const toCalculator = () => capture('assisted_application_to_calculator');

  const goToStep = (nextIndex) => {
    setStepIndex(nextIndex);
    capture('assisted_application_step', { step: nextIndex + 1, step_name: STEPS[nextIndex].name });
    if (nextIndex === RESULT_INDEX && !completedRef.current) {
      completedRef.current = true;
      capture('assisted_application_completed');
    }
  };

  const openGuide = () => {
    setOpen(true);
    if (!startedRef.current) {
      startedRef.current = true;
      capture('assisted_application_started');
      capture('assisted_application_step', { step: stepIndex + 1, step_name: STEPS[stepIndex].name });
    }
  };

  const closeGuide = () => {
    setOpen(false);
    launcherRef.current?.focus();
  };

  const reset = () => {
    setScores(emptyScores());
    setEducationContext('none');
    setFieldErrors({});
    completedRef.current = false;
    goToStep(0);
  };

  const updateScore = (domainId, value, max) => {
    if (value !== '' && !/^\d+$/.test(value)) return;
    const error = value === '' ? null : validateDomainScore(value, max);
    setFieldErrors((previous) => ({ ...previous, [domainId]: error || '' }));
    if (error) return;
    setScores((previous) => ({ ...previous, [domainId]: value }));
  };

  // Foco entra no painel ao abrir; Esc fecha. Sem foco preso: a página continua acessível atrás.
  useEffect(() => {
    if (!open) return undefined;
    closeRef.current?.focus();
    const onKey = (event) => { if (event.key === 'Escape') closeGuide(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <>
      <section aria-labelledby="assistido-titulo" className="band-forest rounded-[var(--radius-block)] px-5 py-7 md:px-8 md:py-9">
        <span aria-hidden="true" className="eyebrow-rule" />
        <h2 id="assistido-titulo" className="mt-4 max-w-[22ch]">Aplicar Mini Mental com ajuda</h2>
        <p className="mt-3 max-w-[58ch] text-[length:var(--fs-17)] leading-relaxed">
          Um guia lateral para consultar durante a aplicação: a sequência, os cuidados de cada domínio e a soma dos pontos que você obteve. Ele acompanha a versão do instrumento adotada na sua prática — não mostra os itens, as palavras nem as figuras do teste.
        </p>
        <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3">
          <button ref={launcherRef} type="button" onClick={openGuide} aria-expanded={open} aria-controls="assistido-painel" className="btn-on-forest">
            {open ? 'Guia aberto ao lado' : 'Abrir o guia lateral'}
          </button>
          <Link to="/" onClick={toCalculator} className="inline-flex min-h-11 items-center underline">
            Já conheço a aplicação: ir para a calculadora
          </Link>
        </div>
        <p className="mt-5 text-[length:var(--fs-14)] leading-relaxed">
          Nenhuma pontuação é enviada ou armazenada: a soma fica só nesta aba enquanto ela estiver aberta.
        </p>
      </section>

      {open && (
        <aside
          id="assistido-painel"
          aria-labelledby="assistido-painel-titulo"
          className="fixed inset-x-0 bottom-[var(--novidades-h,0px)] z-[46] flex max-h-[min(72dvh,640px)] flex-col rounded-t-[var(--radius-block)] border-t border-[var(--line)] bg-[var(--surface)] no-print min-[1200px]:inset-x-auto min-[1200px]:bottom-0 min-[1200px]:right-0 min-[1200px]:top-14 min-[1200px]:w-[400px] min-[1200px]:max-h-none min-[1200px]:rounded-none min-[1200px]:border-l min-[1200px]:border-t-0"
        >
          <div className="band-forest flex items-start justify-between gap-3 rounded-t-[var(--radius-block)] px-5 py-4 min-[1200px]:rounded-none">
            <div className="min-w-0">
              <p className="text-[length:var(--fs-13)] font-medium">Guia lateral · não substitui o instrumento</p>
              <h2 id="assistido-painel-titulo" className="mt-0.5 !text-[length:var(--fs-20)]">Aplicação assistida</h2>
            </div>
            <button ref={closeRef} type="button" onClick={closeGuide} aria-label="Fechar guia" className="-mr-2 -mt-1 inline-flex h-11 w-11 shrink-0 items-center justify-center text-[var(--on-forest)]">
              <SafeIcon name="X" className="h-5 w-5" />
            </button>
          </div>

          <div className="flex items-baseline justify-between gap-3 border-b border-[var(--line)] px-5 py-3">
            <p className="flex items-baseline gap-2">
              <span className="sr-only">Total informado:</span>
              <span className="font-display text-[length:var(--fs-40)] leading-none text-[var(--accent)] tabular">{total}</span>
              <span className="text-[length:var(--fs-14)] text-[var(--ink-2)]">de 30</span>
            </p>
            <p className="text-[length:var(--fs-13)] text-[var(--ink-2)] tabular">{informedCount} de {DOMAINS_CONFIG.length} domínios informados</p>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
            <ol aria-label="Etapas da aplicação" className="ml-1 border-l border-[var(--line)]">
              {STEPS.map((step, index) => {
                const active = index === stepIndex;
                const fraction = stepFraction(step, scores);
                return (
                  <li key={step.name}>
                    <button
                      type="button"
                      onClick={() => goToStep(index)}
                      aria-current={active ? 'step' : undefined}
                      aria-expanded={active}
                      aria-controls={`assistido-etapa-${index}`}
                      className={`${stepButtonBase} ${active ? 'border-[var(--accent)] font-semibold text-[var(--ink)]' : 'border-transparent text-[var(--ink-2)] hover:text-[var(--ink)]'}`}
                    >
                      <span>{step.name}</span>
                      {fraction && <span className="mono text-[length:var(--fs-13)] text-[var(--ink-2)]">{fraction}</span>}
                    </button>

                    {active && (
                      <div id={`assistido-etapa-${index}`} className="mb-3 mt-1 border-l-2 border-transparent pl-4 pr-1 text-[length:var(--fs-15)] leading-relaxed text-[var(--ink-2)]">
                        {step.type === 'preparation' && (
                          <div className="space-y-4">
                            <div>
                              <p className="font-semibold text-[var(--ink)]">Antes de começar</p>
                              <p>Defina a finalidade da avaliação, confirme qual versão do instrumento será utilizada e reduza barreiras evitáveis de comunicação, visão, audição e ambiente.</p>
                            </div>
                            <div>
                              <p className="font-semibold text-[var(--ink)]">Durante a aplicação</p>
                              <p>Mantenha a administração prevista na versão adotada. Quando uma tarefa depender de estímulo específico, utilize os estímulos previstos nessa versão.</p>
                            </div>
                            <div>
                              <p className="font-semibold text-[var(--ink)]">Evite</p>
                              <p>Improvisar instruções, substituir tarefas ou oferecer pistas com o objetivo de facilitar a execução.</p>
                            </div>
                            <p className="text-[length:var(--fs-14)]">Abra qualquer domínio na lista quando chegar nele; a ordem aqui é só sugestão.</p>
                          </div>
                        )}

                        {step.type === 'domains' && (
                          <div className="space-y-5">
                            {step.domainIds.map((domainId) => {
                              const domain = domainById(domainId);
                              const guidance = DOMAIN_GUIDANCE[domainId];
                              const error = fieldErrors[domainId];
                              return (
                                <fieldset key={domain.id} className="border-t border-[var(--line)] pt-3 first:border-t-0 first:pt-0">
                                  <legend className="font-semibold text-[var(--ink)]">
                                    {domain.label} <span className="mono font-normal text-[var(--ink-2)]">máx. {domain.max}</span>
                                  </legend>
                                  <dl className="mt-2 space-y-2 text-[length:var(--fs-14)]">
                                    <div><dt className="inline font-semibold text-[var(--ink)]">O que avalia: </dt><dd className="inline">{guidance.evaluates}</dd></div>
                                    <div><dt className="inline font-semibold text-[var(--ink)]">Durante a aplicação: </dt><dd className="inline">{guidance.conduct}</dd></div>
                                    <div><dt className="inline font-semibold text-[var(--ink)]">Cuidado: </dt><dd className="inline">{guidance.caution}</dd></div>
                                    <div><dt className="inline font-semibold text-[var(--ink)]">Erro frequente: </dt><dd className="inline">{guidance.frequentError}</dd></div>
                                  </dl>
                                  <label htmlFor={`assisted-${domain.id}`} className="mt-4 block font-semibold text-[var(--ink)]">Pontuação obtida</label>
                                  <input
                                    id={`assisted-${domain.id}`}
                                    type="number"
                                    inputMode="numeric"
                                    min="0"
                                    max={domain.max}
                                    step="1"
                                    value={scores[domain.id]}
                                    onChange={(event) => updateScore(domain.id, event.target.value, domain.max)}
                                    aria-describedby={`assisted-${domain.id}-help`}
                                    aria-invalid={error ? true : undefined}
                                    className={`${fieldClass} tabular max-w-[8rem]`}
                                  />
                                  <p id={`assisted-${domain.id}-help`} className="mt-1.5 text-[length:var(--fs-13)]">Inteiro entre 0 e {domain.max}, conforme a versão utilizada.</p>
                                  {error && <p role="alert" className="mt-1.5 text-[length:var(--fs-13)] font-semibold text-[var(--miss)]">{error}</p>}
                                </fieldset>
                              );
                            })}
                          </div>
                        )}

                        {step.type === 'context' && (
                          <div className="space-y-4">
                            <p>Escolaridade não muda a sequência da aplicação, mas contextualiza a interpretação. Esta seleção fica só nesta aba e não é enviada ao analytics.</p>
                            <fieldset>
                              <legend className="font-semibold text-[var(--ink)]">Contexto de escolaridade</legend>
                              <label htmlFor="assisted-education" className="mt-2 block text-[length:var(--fs-14)] font-semibold text-[var(--ink)]">Faixa informada</label>
                              <select
                                id="assisted-education"
                                value={educationContext}
                                onChange={(event) => setEducationContext(event.target.value)}
                                className={fieldClass}
                              >
                                {EDUCATION_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                              </select>
                            </fieldset>
                            <p className="text-[length:var(--fs-14)]">Registre à parte condições clínicas, sensoriais, linguísticas ou ambientais que possam ter interferido. Não ajuste a pontuação por conta própria para compensá-las.</p>
                          </div>
                        )}

                        {step.type === 'result' && (
                          <div className="space-y-4">
                            <p>
                              <span className="font-semibold text-[var(--ink)]">Total informado: {total} de 30.</span>
                              {informedCount < DOMAINS_CONFIG.length && ` ${DOMAINS_CONFIG.length - informedCount} ${DOMAINS_CONFIG.length - informedCount === 1 ? 'domínio ainda sem pontuação' : 'domínios ainda sem pontuação'} — o total pode estar subestimado.`}
                            </p>
                            <dl className="divide-y divide-[var(--line)] border-y border-[var(--line)] text-[length:var(--fs-14)]">
                              {DOMAINS_CONFIG.map((domain) => (
                                <div key={domain.id} className="flex items-baseline justify-between gap-4 py-1.5">
                                  <dt>{domain.label}</dt>
                                  <dd className="mono text-[var(--ink)]">{scores[domain.id] === '' ? '—' : scores[domain.id]}/{domain.max}</dd>
                                </div>
                              ))}
                            </dl>
                            <p className="text-[length:var(--fs-14)]"><span className="font-semibold text-[var(--ink)]">Escolaridade: </span>{selectedEducation?.label || 'Não informada'}</p>
                            <p className="text-[length:var(--fs-14)]">A interpretação deve seguir a versão aplicada e o contexto clínico. Este total corresponde apenas aos pontos que você informou; o guia não cria nem adapta tarefas do instrumento.</p>
                            <div className="flex flex-col gap-2">
                              <Link to="/" onClick={toCalculator} className="btn-primary no-underline">Levar o total para a calculadora</Link>
                              <Link to="/interpretacao-dos-resultados" className="btn-secondary no-underline">Ver interpretação completa</Link>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ol>
          </div>

          <div className="flex items-center justify-between gap-2 border-t border-[var(--line)] px-5 py-3">
            <button type="button" onClick={() => goToStep(Math.max(0, stepIndex - 1))} disabled={stepIndex === 0} className={`${navButton} btn-secondary`}>
              Voltar
            </button>
            <button type="button" onClick={reset} className="inline-flex min-h-11 items-center px-2 text-[length:var(--fs-14)] text-[var(--ink-2)] underline">
              Limpar
            </button>
            {currentStep.type !== 'result' ? (
              <button type="button" onClick={() => goToStep(Math.min(stepIndex + 1, RESULT_INDEX))} className={`${navButton} btn-primary`}>
                Continuar
              </button>
            ) : (
              <button type="button" onClick={closeGuide} className={`${navButton} btn-secondary`}>
                Fechar
              </button>
            )}
          </div>
        </aside>
      )}
    </>
  );
}
